import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForgetPasswordMutation, useVerifyOtpMutation, useResetPasswordMutation } from '../redux/apiSlice';

const ForgetPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);
  const navigate = useNavigate();

  // Forget password mutation
  const [forgetPassword, { isLoading: isSendingCode }] = useForgetPasswordMutation();
  
  // OTP verification mutation
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  
  // Reset password mutation
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();

  // Timer for resend code
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  // Timer for blocking
  useEffect(() => {
    let timer;
    if (blockTimer > 0) {
      timer = setTimeout(() => setBlockTimer(blockTimer - 1), 1000);
    } else if (isBlocked && blockTimer === 0) {
      setIsBlocked(false);
      setResendAttempts(0);
    }
    return () => clearTimeout(timer);
  }, [blockTimer, isBlocked]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    
    try {
      // Validate email
      if (!email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Prepare data according to backend
      const emailData = {
        email: email.trim()
      };

      console.log('📤 Sending forget password request:', emailData);

      // Call the forget password API
      const response = await forgetPassword(emailData).unwrap();
      
      console.log('✅ Forget password response:', response);

      if (response.message) {
        setSuccessMessage(response.message || 'OTP sent to your email successfully!');
        setIsLoading(false);
        setStep(2);
        setResendTimer(30); // Start 30 second timer
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error('Failed to send OTP. Please try again.');
      }

    } catch (error) {
      console.error('❌ Send code error:', error);
      setIsLoading(false);
      
      if (error.data) {
        // Handle specific error messages from API
        if (error.data.message === 'Instructor not found') {
          setError('No account found with this email address.');
        } else if (error.data.message === 'Error sending OTP') {
          setError('Failed to send OTP. Please try again.');
        } else {
          setError(error.data.message || 'Failed to send verification code. Please try again.');
        }
      } else if (error.status === 404) {
        setError('No account found with this email address.');
      } else if (error.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setError('Network error. Please check your connection.');
      } else {
        setError(error.message || 'Failed to send verification code. Please try again.');
      }
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    
    try {
      // Validate OTP code
      if (code.length !== 6) {
        throw new Error('Please enter a valid 6-digit code');
      }

      // Prepare OTP data according to backend
      const otpData = {
        otp: code.trim()
      };

      console.log('📤 Verifying OTP:', otpData);

      // Call the OTP verification API
      const response = await verifyOtp(otpData).unwrap();
      
      console.log('✅ OTP verification response:', response);

      if (response.message) {
        setSuccessMessage(response.message || 'OTP verified successfully!');
        setIsLoading(false);
        setStep(3);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error('Failed to verify OTP. Please try again.');
      }

    } catch (error) {
      console.error('❌ Verify code error:', error);
      setIsLoading(false);
      
      if (error.data) {
        // Handle specific error messages from API
        if (error.data.message === 'Invalid or expired OTP') {
          setError('Invalid or expired OTP. Please try again.');
        } else if (error.data.message === 'Account locked due to too many failed attempts') {
          setError('Account locked due to too many failed attempts. Please try again later.');
        } else {
          setError(error.data.message || 'Failed to verify OTP. Please try again.');
        }
      } else if (error.status === 404) {
        setError('Invalid or expired OTP. Please try again.');
      } else if (error.status === 403) {
        setError('Account locked due to too many failed attempts. Please try again later.');
      } else if (error.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setError('Network error. Please check your connection.');
      } else {
        setError(error.message || 'Failed to verify OTP. Please try again.');
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare password data according to backend
      const passwordData = {
        newPassword: newPassword.trim()
      };

      console.log('📤 Resetting password:', passwordData);

      // Call the reset password API
      const response = await resetPassword(passwordData).unwrap();
      
      console.log('✅ Reset password response:', response);

      if (response.message) {
        setSuccessMessage(response.message || 'Password reset successfully!');
        setIsLoading(false);
        setStep(4);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        throw new Error('Failed to reset password. Please try again.');
      }

    } catch (error) {
      console.error('❌ Reset password error:', error);
      setIsLoading(false);
      
      if (error.data) {
        // Handle specific error messages from API
        if (error.data.message === 'No verified instructor found') {
          setError('No verified account found. Please complete the verification process first.');
        } else if (error.data.message === 'Error resetting password') {
          setError('Failed to reset password. Please try again.');
        } else {
          setError(error.data.message || 'Failed to reset password. Please try again.');
        }
      } else if (error.status === 400) {
        setError('No verified account found. Please complete the verification process first.');
      } else if (error.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setError('Network error. Please check your connection.');
      } else {
        setError(error.message || 'Failed to reset password. Please try again.');
      }
    }
  };

  const handleResendCode = async () => {
    if (isBlocked) {
      setError(`Too many attempts. Please try again in ${formatTime(blockTimer)}`);
      return;
    }

    if (resendTimer > 0) {
      setError(`Please wait ${resendTimer} seconds before requesting a new code`);
      return;
    }

    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      // Prepare data according to backend
      const emailData = {
        email: email.trim()
      };

      console.log('📤 Resending forget password request:', emailData);

      // Call the forget password API again
      const response = await forgetPassword(emailData).unwrap();
      
      console.log('✅ Resend code response:', response);

      // Increment resend attempts
      const newAttempts = resendAttempts + 1;
      setResendAttempts(newAttempts);
      
      // Check if user should be blocked
      if (newAttempts >= 4) {
        setIsBlocked(true);
        setBlockTimer(24 * 60 * 60); // 24 hours in seconds
        setError('Too many resend attempts. You are blocked for 24 hours.');
      } else {
        // Reset timer for next resend
        setResendTimer(30);
        setSuccessMessage(response.message || 'Verification code has been resent to your email.');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('❌ Resend code error:', error);
      setIsLoading(false);
      
      if (error.data) {
        // Handle specific error messages from API
        if (error.data.message === 'Instructor not found') {
          setError('No account found with this email address.');
        } else if (error.data.message === 'Error sending OTP') {
          setError('Failed to send OTP. Please try again.');
        } else {
          setError(error.data.message || 'Failed to resend code. Please try again.');
        }
      } else if (error.status === 404) {
        setError('No account found with this email address.');
      } else if (error.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.status === 'FETCH_ERROR') {
        setError('Network error. Please check your connection.');
      } else {
        setError('Failed to resend code. Please try again.');
      }
    }
  };

  const formatTime = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    } else if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {step === 1 && 'Reset Your Password'}
              {step === 2 && 'Enter Verification Code'}
              {step === 3 && 'Create New Password'}
              {step === 4 && 'Password Reset Successful'}
            </h2>
            <Link 
              to="/login" 
              className="text-white hover:text-green-100 transition-colors duration-300 p-2 hover:bg-green-600 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                  step >= stepNumber
                    ? 'bg-green-500 border-green-500 text-white shadow-lg'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {step > stepNumber ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-bold">{stepNumber}</span>
                  )}
                </div>
                {stepNumber < 4 && (
                  <div className={`flex-1 h-1 mx-2 transition-all duration-500 ${
                    step > stepNumber ? 'bg-green-500' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mx-6 mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
            <p className="text-sm flex items-center text-green-600">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {successMessage}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-6 mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm flex items-center text-red-600">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-600">Enter your email address and we'll send you a verification code.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || isSendingCode}
                className="w-full py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {(isLoading || isSendingCode) ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending Code...</span>
                  </div>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          )}

          {/* Step 2: Enter Verification Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-gray-600">We sent a 6-digit code to <strong>{email}</strong></p>
                <p className="text-sm text-gray-500 mt-1">Enter the code below to verify your identity.</p>
                
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading || resendTimer > 0 || isBlocked}
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isBlocked 
                        ? 'text-red-600 cursor-not-allowed' 
                        : resendTimer > 0 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-green-600 hover:text-green-700'
                    }`}
                  >
                    {isLoading ? (
                      'Resending...'
                    ) : isBlocked ? (
                      `Blocked (${formatTime(blockTimer)})`
                    ) : resendTimer > 0 ? (
                      `Resend available in ${resendTimer}s`
                    ) : (
                      `Resend Code (${4 - resendAttempts} attempts left)`
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 text-center text-lg font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 px-6 text-base font-semibold text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isVerifyingOtp || code.length !== 6}
                  className="flex-1 py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {(isLoading || isVerifyingOtp) ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Create New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <p className="text-gray-600">Create a new password for your account.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 pr-12"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={toggleNewPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-green-500 transition-colors duration-300"
                  >
                    {showNewPassword ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-300 pr-12"
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-green-500 transition-colors duration-300"
                  >
                    {showConfirmPassword ? (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 px-6 text-base font-semibold text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isResettingPassword}
                  className="flex-1 py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {(isLoading || isResettingPassword) ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Resetting...</span>
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Successful!</h4>
                <p className="text-gray-600">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
              </div>

              <button
                onClick={handleBackToLogin}
                className="w-full py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Continue to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;